import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  InputNumber,
  Popconfirm,
  Space,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { collection, doc, getDocs, writeBatch } from "firebase/firestore";
import { db } from "../../firebase/firebase";

const { Text, Paragraph } = Typography;

const FIELD_WARN = "chat_warn";
const FIELD_LIMIT = "chat_limit";
/** Firestore writeBatch の上限は 500。余裕を持って分割する。 */
const BATCH_SIZE = 400;

type ChatTimeFormValues = {
  chat_warn: number;
  chat_limit: number;
};

function toNonNegativeInt(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value));
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
  }
  return 0;
}

/**
 * 全デバイス共通のチャット時間制限（devices.chat_warn / chat_limit）を一括設定する。
 * 単位は分。0 は制限なし（タブレット側の仕様と一致）。
 */
const Setting_ChatTimeLimit: React.FC = () => {
  const [form] = Form.useForm<ChatTimeFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deviceCount, setDeviceCount] = useState(0);
  const [sampleDeviceId, setSampleDeviceId] = useState<string | null>(null);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "devices"));
      const active = snapshot.docs.filter((d) => d.data().isDeleted !== true);
      setDeviceCount(active.length);

      let warn = 0;
      let limit = 0;
      let sampleId: string | null = null;
      for (const d of active) {
        const data = d.data();
        const w = toNonNegativeInt(data[FIELD_WARN]);
        const l = toNonNegativeInt(data[FIELD_LIMIT]);
        if (w > 0 || l > 0 || data[FIELD_WARN] != null || data[FIELD_LIMIT] != null) {
          warn = w;
          limit = l;
          sampleId = d.id;
          break;
        }
      }
      setSampleDeviceId(sampleId);
      form.setFieldsValue({ chat_warn: warn, chat_limit: limit });
    } catch (e) {
      console.error(e);
      message.error("現在の設定の読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadCurrent();
  }, [loadCurrent]);

  const applyToAllDevices = async (values: ChatTimeFormValues) => {
    const warn = toNonNegativeInt(values.chat_warn);
    const limit = toNonNegativeInt(values.chat_limit);

    setSaving(true);
    try {
      const snapshot = await getDocs(collection(db, "devices"));
      const targets = snapshot.docs.filter((d) => d.data().isDeleted !== true);
      if (targets.length === 0) {
        message.warning("更新対象のデバイスがありません");
        return;
      }

      const payload = {
        [FIELD_WARN]: warn,
        [FIELD_LIMIT]: limit,
      };

      for (let i = 0; i < targets.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = targets.slice(i, i + BATCH_SIZE);
        for (const d of chunk) {
          batch.update(doc(db, "devices", d.id), payload);
        }
        await batch.commit();
      }

      setDeviceCount(targets.length);
      setSampleDeviceId(targets[0]?.id ?? null);
      form.setFieldsValue({ chat_warn: warn, chat_limit: limit });
      message.success(
        `${targets.length} 台のデバイスにチャット時間制限を保存しました（警告 ${warn} 分 / 上限 ${limit} 分）`
      );
    } catch (e) {
      console.error(e);
      message.error("全デバイスへの保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await applyToAllDevices(values);
    } catch {
      // validateFields の失敗は Form が表示する
    }
  };

  return (
    <Card loading={loading}>
      <Paragraph>
        全デバイスの <Text code>chat_warn</Text>（警告時間）と{" "}
        <Text code>chat_limit</Text>（上限時間）を分単位で一括設定します。
        既存の値がある場合も上書きします。論理削除済みデバイスは対象外です。
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        値が <Text strong>0</Text> の項目は「制限なし」として扱われます。
        タブレットはスリープ復帰時に設定を読み取ります。
      </Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`更新対象デバイス: ${deviceCount} 台`}
        description={
          sampleDeviceId
            ? `画面に表示中の初期値は、既存設定があるデバイス（例: ${sampleDeviceId}）から読み込んでいます。`
            : "まだどのデバイスにも設定がないため、初期値は 0 です。"
        }
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{ chat_warn: 0, chat_limit: 0 }}
        style={{ maxWidth: 420 }}
      >
        <Form.Item
          name="chat_warn"
          label="警告時間（分）"
          rules={[
            { required: true, message: "警告時間を入力してください" },
            { type: "number", min: 0, message: "0 以上の整数で入力してください" },
          ]}
          extra="この時間以上になると警告ダイアログを表示します。0 で警告なし。"
        >
          <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="chat_limit"
          label="上限時間（分）"
          rules={[
            { required: true, message: "上限時間を入力してください" },
            { type: "number", min: 0, message: "0 以上の整数で入力してください" },
          ]}
          extra="この時間以上になると新しい会話を開始できません。0 で上限なし。"
        >
          <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          shouldUpdate={(prev, next) =>
            prev.chat_warn !== next.chat_warn || prev.chat_limit !== next.chat_limit
          }
          noStyle
        >
          {() => {
            const warn = toNonNegativeInt(form.getFieldValue("chat_warn"));
            const limit = toNonNegativeInt(form.getFieldValue("chat_limit"));
            if (warn > 0 && limit > 0 && warn > limit) {
              return (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="警告時間が上限時間より大きいです。タブレットでは上限到達が先に判定されます。"
                />
              );
            }
            return null;
          }}
        </Form.Item>

        <Space>
          <Popconfirm
            title="全デバイスへ上書き保存しますか？"
            description={`警告 ${toNonNegativeInt(form.getFieldValue("chat_warn"))} 分 / 上限 ${toNonNegativeInt(form.getFieldValue("chat_limit"))} 分 を ${deviceCount} 台に書き込みます。`}
            okText="保存する"
            cancelText="キャンセル"
            onConfirm={() => void handleSave()}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={loading || deviceCount === 0}
            >
              全デバイスに保存
            </Button>
          </Popconfirm>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void loadCurrent()}
            disabled={saving}
          >
            再読み込み
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default Setting_ChatTimeLimit;
